/**
 * System registry — mirrors the six suite ids on the shell and
 * marks whether each is wired to a live data backend. While the
 * Events suite work is ongoing it lives here as `mock` too; flip
 * to `live` per system as backends come online.
 */

export type DataStatus = 'live' | 'mock';

export interface SystemDescriptor {
  id: string;
  label: string;
  route: string;
  /** Default data-status when no per-module override exists. */
  dataStatus: DataStatus;
  /** Per-module overrides — flip individual modules to 'live' as they wire up. */
  moduleStatus?: Record<string, DataStatus>;
}

export const SYSTEMS: SystemDescriptor[] = [
  {
    id: 'events',
    label: 'אירועים ושטח',
    route: '/system/events',
    dataStatus: 'mock',
    moduleStatus: {
      // First end-to-end live module wired to /v1/contract/funnel.
      funnel: 'live',
      // A1 scorecard wired to /v1/contract/organizers.
      organizers: 'live',
      // National coverage map wired to /v1/contract/coverage.
      coverage: 'live',
      // A3 format comparison wired to /v1/contract/formats.
      formats: 'live',
      // D1 channels leaderboard wired to /v1/contract/channels.
      channels: 'live',
      // B2 trends time-series wired to /v1/contract (trends + campaign_trend + week_labels).
      trends: 'live',
      // A2 speaker × format matrix wired to /v1/contract/speakers (alias of speaker_by_format).
      speakers: 'live',
    },
  },
  { id: 'audience',    label: 'קהל ובוחרים',   route: '/system/audience',    dataStatus: 'mock' },
  { id: 'polls',       label: 'סקרים ופאנלים', route: '/system/polls',       dataStatus: 'mock' },
  { id: 'macro',       label: 'מאקרו ומדיניות', route: '/system/macro',       dataStatus: 'mock' },
  { id: 'media',       label: 'מדיה ומסר',     route: '/system/media',       dataStatus: 'mock' },
  { id: 'competition', label: 'תחרות',          route: '/system/competition', dataStatus: 'mock' },
];

export function findSystem(id: string): SystemDescriptor | undefined {
  return SYSTEMS.find((s) => s.id === id);
}

/**
 * Resolve the effective data-status for a (system, module) pair. Falls
 * back to the system-level default when no per-module override exists.
 */
export function moduleDataStatus(systemId: string, moduleId: string): DataStatus {
  const sys = findSystem(systemId);
  if (!sys) return 'mock';
  return sys.moduleStatus?.[moduleId] ?? sys.dataStatus;
}
